import React from "react";
import foto from "../assets/profielfoto2025.jpg";
import { motion } from "framer-motion";

import "../css/Header.css";

const updates = [
    { date: "September 2026", text: "I served as an examiner for the doctoral defense of Florian Flükiger-Fuchs at ETH Zürich, and I gave a talk at the Institut für Verkehrsplanung und Transportsysteme." },
    { date: "July 2026", text: <>I gave a talk at the IFORS conference in Vienna, and participated in the <a href="https://www.dagstuhl.de/en/seminars/seminar-calendar/seminar-details/26301" target="_blank" rel="noopener noreferrer">Dagstuhl seminar on Algorithmic Advances for a New Era of Data-Driven, Multi-Modal Transit Systems</a>.</> },
    { date: "June 2026", text: <>New working paper: '<a href="/linePlanningAtScale.pdf" target="_blank" rel="noopener noreferrer">Line Planning at Scale: Models, Methods, and Insights</a>', joint work with Bart van Rossum.</> },
    { date: "February 2026", text: <>I visited <a href="https://people.epfl.ch/michel.bierlaire" target="_blank" rel="noopener noreferrer">Michel Bierlaire</a> at EPFL in Lausanne to work on integrating behavioral models in line planning.</> },
    { date: "January 2026", text: "Seminar at the AI & Mobility Lab at Utrecht University." },
  { date: "December 2025", text: <>Together with <a href="https://www.universiteitvannederland.nl/" target="_blank" rel="noopener noreferrer">Universiteit van Nederland</a>, I created a video explaining why the Dutch railway timetable is still computed by hand.</>, videoUrl: "https://www.youtube.com/watch?v=udVHtt5XrrY&" },
  { date: "November 2025", text: <>I gave a seminar at the MIT Center for Transportation and Logistics in Boston, and also presented at INFORMS in Atlanta. At the TSL Business meeting, my co-authors and I received the Transportation Science Best Paper of the Year award in the Modes & Industries area for our <a href="https://pubsonline.informs.org/doi/abs/10.1287/trsc.2022.0253" target="_blank" rel="noopener noreferrer">paper on electric vehicle scheduling</a>.</> },
  { date: "September 2025", text: <>The paper '<a href="fpap.pdf" target="_blank" rel="noopener noreferrer">The Fair Periodic Assignment Problem</a>', co-authored with Bart van Rossum, won the ATMOS Best Paper award. I presented this work and accepted the award at the ATMOS conference in Warsaw, Poland.</> },
  { date: "August 2025", text: <>My <a href="https://doi.org/10.1287/ijoc.2024.0698" target="_blank" rel="noopener noreferrer">paper on dynamic discretization discovery for the multi-depot vehicle scheduling problem with trip shifting</a> has been accepted in the INFORMS Journal on Computing. I also uploaded a new version of my <a href="/unifiedApproach.pdf" target="_blank" rel="noopener noreferrer">paper on passenger routing and evaluation in public transport systems</a>.</> },
    { date: "July 2025", text: "I have been awarded the Veni Grant by NWO (Nederlandse Organisatie voor Wetenschappelijk Onderzoek)" },
    { date: "June 2025", text: "I gave two talks at the 12th TRISTAN symposium in Okinawa, Japan." },
    { date: "March–May 2025", text: "Seminars at CWI, Tilburg University, and Erasmus University Rotterdam." },
    { date: "January 2025", text: "Received the Industria Best Teacher Award." },
    { date: "November 2024", text: "Seminar at Amsterdam Business School." },
];
const listVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

const Header = () => {
  return (
    <section id="header" className="website-header">
        <div className="header-container">
            <header className="header-text">
                <h1>
                    Rolf Nelson van Lieshout
                </h1>
                <h2>
                    Assistant Professor of Transport and Mobility
                </h2>
                <p>
                I joined the <a href="https://www.tue.nl/en/research/research-groups/industrial-engineering/operations-planning-accounting-control" target="_blank" rel="noopener noreferrer">OPAC group</a> at Eindhoven University of Technology in January 2022, after obtaining my 
                PhD in Operations Research <i>cum laude</i> from the <a href="https://www.eur.nl/en/ese/department-econometrics" target="_blank" rel="noopener noreferrer">Econometric Institute</a> at Erasmus University Rotterdam.
                </p>
                <p>
                My long-term research goal is to enhance the design and operation of public transport systems.
                Within this context, my research operates on the intersection between operations research and economics; 
                to improve decision-making in public transport, I develop advanced optimization algorithms that integrate economic theories on
                welfare and traveler behavior. My research is supported by the <a href="https://www.nwo.nl/en/research-funding/programmes/veni" target="_blank" rel="noopener noreferrer">Veni grant</a> from the Dutch Research Council (NWO).
                </p>
                <p>
                    My teaching focuses on courses related to transport and logistics, within TU/e's master program on <i>Operations Management and Logistics</i>. 
                    In 2025, I was honoured as the <a href="https://industria.tue.nl/educational-awards/" target="_blank" rel="noopener noreferrer">Industria Best Teacher</a> within this program.
                </p>
            </header>
            <div className="header-image">
                <img src={foto} alt="foto" width="300" height="auto" />
            </div>
        </div>
    <div className="updates-section">
    <h2>Updates</h2>
    <motion.ul
        variants={listVariants}
        initial="hidden"
        animate="visible"
        style={{ listStyle: "none", paddingLeft: 0 }}
    >
        {updates.map((update, idx) => {
            const embedUrl = getYouTubeEmbedUrl(update.videoUrl);
            return (
                <motion.li key={idx} variants={itemVariants} style={{ marginBottom: "1em" }}>
                    <span className="date" style={{ fontWeight: "bold", display: "block" }}>{update.date}</span>
                    {update.text && <p style={{ margin: 0 }}>{update.text}</p>}
                    {embedUrl && (
                        <div className="video-container">
                            <iframe
                                src={embedUrl}
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            ></iframe>
                        </div>
                    )}
                </motion.li>
            );
        })}
    </motion.ul>
    </div>

    </section>
  );
};

export default Header;
